// MPEG-1/2/2.5 audio, Layers I, II and III (MP1, MP2, MP3).
//
// An MPEG audio file is just a run of self-contained frames. Each frame starts
// with a 32-bit header that begins with 11 set bits (the "sync word"). There is
// no file header: tags (ID3v2, APEv2, ID3v1) sit before or after the frames, and
// the optional Xing/Info/VBRI frame is a normal-looking frame that holds silence
// plus facts about the whole stream.
//
// Performance: a 100 MB file has ~250,000 frames. Each frame is one small
// object (Mp3Frame). Its name, summary, fields and children are prototype
// getters, so nothing else is allocated until the UI asks for it.
import { ascii, BitFields, field, fmtBytes, fmtHz, fmtKbps, fmtSec, hex, matches, plural, u16be, u32be, u32le, utf8Str } from './bits';
import { id3Explanations, parseId3v1, parseId3v2 } from './id3';
import type { Explanation, Field, FormatParser, Note, ParsedFile, Picture, Segment, SegmentLink, Tag } from './types';

// ---------------------------------------------------------------------------
// Header tables

const BITRATES = {
  v1l1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
  v1l2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
  v1l3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
  v2l1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
  v2l23: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
};
// Indexed by the 2-bit version field: 0 = MPEG-2.5, 1 = reserved, 2 = MPEG-2, 3 = MPEG-1.
const SAMPLE_RATES = [[11025, 12000, 8000], [0, 0, 0], [22050, 24000, 16000], [44100, 48000, 32000]];
const VERSION_NAMES = ['MPEG-2.5', 'reserved', 'MPEG-2', 'MPEG-1'];
// Indexed by the 2-bit layer field: 1 = Layer III, 2 = Layer II, 3 = Layer I.
const LAYER_NAMES = ['reserved', 'Layer III', 'Layer II', 'Layer I'];
const MODE_NAMES = ['Stereo', 'Joint stereo', 'Dual channel', 'Mono'];
const EMPHASIS = ['none', '50/15 µs', 'reserved', 'CCITT J.17'];

// Header bit accessors (h is the 32-bit header as an unsigned number).
const hVer = (h: number) => (h >>> 19) & 3;
const hLayer = (h: number) => (h >>> 17) & 3;
const hProt = (h: number) => (h >>> 16) & 1; // 0 = CRC present
const hBr = (h: number) => (h >>> 12) & 15;
const hSr = (h: number) => (h >>> 10) & 3;
const hPad = (h: number) => (h >>> 9) & 1;
const hMode = (h: number) => (h >>> 6) & 3;

function bitrateTable(ver: number, layer: number): number[] {
  if (ver === 3) return layer === 3 ? BITRATES.v1l1 : layer === 2 ? BITRATES.v1l2 : BITRATES.v1l3;
  return layer === 3 ? BITRATES.v2l1 : BITRATES.v2l23;
}
export const sampleRateOf = (h: number) => SAMPLE_RATES[hVer(h)][hSr(h)];
export const samplesPerFrame = (h: number) => (hLayer(h) === 3 ? 384 : hLayer(h) === 2 || hVer(h) === 3 ? 1152 : 576);
const kbpsOf = (h: number) => bitrateTable(hVer(h), hLayer(h))[hBr(h)];
/** Layer III side-information size in bytes. */
const sideInfoSize = (h: number) => (hVer(h) === 3 ? (hMode(h) === 3 ? 17 : 32) : hMode(h) === 3 ? 9 : 17);

/** True when the 32 bits form a legal MPEG audio frame header. */
export function validHeader(h: number): boolean {
  return (h >>> 21) === 0x7ff && hVer(h) !== 1 && hLayer(h) !== 0 && hBr(h) !== 15 && hSr(h) !== 3 && (h & 3) !== 2;
}
/** The parts of the header that stay the same for a whole stream: sync, version, layer, sample rate. */
const streamKey = (h: number) => (h & 0xfffe0c00) >>> 0;

/** Frame length in bytes. `free` is the slot count for free-format streams (0 if unknown). */
function frameLength(h: number, free: number): number {
  const layer = hLayer(h), pad = hPad(h);
  const kbps = kbpsOf(h);
  if (kbps === 0) return free > 0 ? free + (layer === 3 ? pad * 4 : pad) : 0;
  const sr = sampleRateOf(h);
  if (layer === 3) return (Math.floor((12000 * kbps) / sr) + pad) * 4;
  return Math.floor(((samplesPerFrame(h) / 8) * kbps * 1000) / sr) + pad;
}

// ---------------------------------------------------------------------------
// CRCs

/** CRC-16 as used by MPEG audio frames: polynomial 0x8005, init 0xFFFF, MSB first. */
function crc16Mpeg(b: Uint8Array, s: number, e: number, crc = 0xffff): number {
  for (let i = s; i < e; i++) {
    crc ^= b[i] << 8;
    for (let k = 0; k < 8; k++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x8005) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}
let arcTable: Uint16Array | null = null;
/** CRC-16/ARC (reflected 0x8005, init 0), as used by the LAME tag. */
function crc16Arc(b: Uint8Array, s: number, e: number): number {
  if (!arcTable) {
    arcTable = new Uint16Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ 0xa001 : c >>> 1;
      arcTable[n] = c;
    }
  }
  let crc = 0;
  for (let i = s; i < e; i++) crc = (crc >>> 8) ^ arcTable[(crc ^ b[i]) & 0xff];
  return crc;
}

// ---------------------------------------------------------------------------
// Per-frame decoding (only runs when the UI asks)

function headerFields(b: Uint8Array, o: number): Field[] {
  const h = u32be(b, o);
  const ver = hVer(h), layer = hLayer(h);
  const bf = new BitFields(b, o);
  bf.read('Frame sync', 11, (v) => hex(v, 3), 'Eleven 1-bits that mark the start of every frame, so a player can find frames anywhere.');
  bf.read('MPEG version', 2, (v) => VERSION_NAMES[v], 'MPEG-1 covers 32–48 kHz. MPEG-2 and 2.5 add lower sample rates for speech and low bitrates.');
  bf.read('Layer', 2, (v) => LAYER_NAMES[v], 'Which codec this is: Layer III is MP3, Layer II is MP2.');
  bf.read('Protection', 1, (v) => (v ? 'No CRC' : 'CRC follows'), 'A 0 here means a 16-bit checksum follows the header.');
  bf.read('Bitrate index', 4, (v) => (v === 0 ? 'free format' : v === 15 ? 'invalid' : `${bitrateTable(ver, layer)[v]} kbps`), 'Picks the bitrate from a table. It can change from frame to frame (VBR).');
  bf.read('Sample rate index', 2, (v) => (v === 3 ? 'reserved' : fmtHz(SAMPLE_RATES[ver][v])), 'Picks the sample rate from a table that depends on the MPEG version.');
  bf.read('Padding', 1, (v) => (v ? `yes (+${layer === 3 ? 4 : 1} byte${layer === 3 ? 's' : ''})` : 'no'), 'Adds one slot to this frame so the average bitrate comes out exact.');
  bf.read('Private', 1, undefined, 'Free for any use. Decoders ignore it.');
  bf.read('Channel mode', 2, (v) => MODE_NAMES[v], 'How the channels are stored. Joint stereo can share data between left and right to save bits.');
  const mode = hMode(h);
  bf.read('Mode extension', 2, (v) => {
    if (mode !== 1) return `${v} (not used)`;
    if (layer === 1) return [(v & 2 ? 'M/S on' : 'M/S off'), (v & 1 ? 'intensity on' : 'intensity off')].join(', ');
    return `intensity stereo from band ${4 + v * 4}`;
  }, 'Only for joint stereo: which stereo tricks this frame uses.');
  bf.read('Copyright', 1, (v) => (v ? 'yes' : 'no'), 'A flag that says the audio is copyrighted. Nothing enforces it.');
  bf.read('Original', 1, (v) => (v ? 'original' : 'copy'), 'A flag that says whether this is the original or a copy.');
  bf.read('Emphasis', 2, (v) => EMPHASIS[v], 'Old noise-reduction pre-emphasis. Almost always none.');
  return bf.fields;
}

function sideInfoFields(b: Uint8Array, o: number, h: number): Field[] {
  const v1 = hVer(h) === 3;
  const nch = hMode(h) === 3 ? 1 : 2;
  const bf = new BitFields(b, o);
  bf.read('main_data_begin', v1 ? 9 : 8, (v) => (v === 0 ? '0 (starts in this frame)' : `${v} bytes back`),
    'Where this frame\'s audio data starts: this many bytes before the side info ends, in earlier frames (the bit reservoir).');
  bf.read('private_bits', v1 ? (nch === 1 ? 5 : 3) : nch, undefined, 'Free for private use. Decoders ignore them.');
  if (v1) for (let ch = 0; ch < nch; ch++)
    bf.read(`scfsi (ch ${ch})`, 4, (v) => v.toString(2).padStart(4, '0'), 'Each 1 bit lets granule 1 reuse a band group\'s scale factors from granule 0.');
  const ng = v1 ? 2 : 1;
  for (let gr = 0; gr < ng; gr++) {
    for (let ch = 0; ch < nch; ch++) {
      const pre = `Granule ${gr} · ch ${ch} · `;
      bf.read(pre + 'part2_3_length', 12, (v) => `${v} bits`, 'Bits of main data for this granule and channel: scale factors plus Huffman codes.');
      bf.read(pre + 'big_values', 9, undefined, 'Number of value pairs in the low-frequency region, where most detail is.');
      bf.read(pre + 'global_gain', 8, undefined, 'The quantizer step size. Higher means louder values with less precision.');
      bf.read(pre + 'scalefac_compress', v1 ? 4 : 9, undefined, 'Picks how many bits each scale factor uses.');
      const ws = bf.read(pre + 'window_switching_flag', 1, (v) => (v ? 'yes' : 'no'), 'If 1, this granule uses a special block type (short or transition windows).');
      if (ws) {
        bf.read(pre + 'block_type', 2, (v) => ['reserved', 'start', 'short', 'stop'][v],
          'Window shape. Short blocks (3 × 192 samples) stop "pre-echo" smearing on sharp sounds like drum hits.');
        bf.read(pre + 'mixed_block_flag', 1, (v) => (v ? 'yes' : 'no'), 'If 1, low frequencies use long blocks and high ones use short blocks.');
        for (let r = 0; r < 2; r++) bf.read(pre + `table_select[${r}]`, 5, undefined, 'Which Huffman table codes this region.');
        for (let w = 0; w < 3; w++) bf.read(pre + `subblock_gain[${w}]`, 3, undefined, 'Extra gain for one of the three short windows.');
      } else {
        for (let r = 0; r < 3; r++) bf.read(pre + `table_select[${r}]`, 5, undefined, 'Which Huffman table codes this region.');
        bf.read(pre + 'region0_count', 4, undefined, 'Where the first Huffman region ends (in scale-factor bands).');
        bf.read(pre + 'region1_count', 3, undefined, 'Where the second Huffman region ends.');
      }
      if (v1) bf.read(pre + 'preflag', 1, (v) => (v ? 'yes' : 'no'), 'If 1, a fixed boost is added to high-frequency scale factors.');
      bf.read(pre + 'scalefac_scale', 1, (v) => (v ? 'coarse (×2)' : 'fine (×√2)'), 'The step size of the scale factors.');
      bf.read(pre + 'count1table_select', 1, (v) => (v ? 'B' : 'A'), 'Which table codes the small (−1, 0, 1) values at high frequencies.');
    }
  }
  return bf.fields;
}

/** Child segments of an audio frame (or of a Xing frame, up to the side info). */
function frameParts(b: Uint8Array, s: number, e: number, h: number, withMain: boolean, links?: () => SegmentLink[]): Segment[] {
  const out: Segment[] = [];
  const hdrEnd = Math.min(e, s + 4);
  out.push({ kind: 'header', type: 'mp3.header', name: 'Frame header', start: s, end: hdrEnd, fields: hdrEnd - s === 4 ? headerFields(b, s) : [], summary: hex(h, 8) });
  let p = hdrEnd;
  const crcPresent = hProt(h) === 0;
  let crcSeg: Segment | null = null;
  if (crcPresent && p + 2 <= e) {
    crcSeg = { kind: 'header', type: 'mp3.crc', name: 'CRC', start: p, end: p + 2 };
    out.push(crcSeg);
    p += 2;
  }
  if (hLayer(h) === 1) {
    const si = sideInfoSize(h);
    const siEnd = Math.min(e, p + si);
    const complete = siEnd - p === si;
    const mdb = complete ? (hVer(h) === 3 ? (b[p] << 1) | (b[p + 1] >> 7) : b[p]) : 0;
    out.push({
      kind: 'header', type: 'mp3.sideinfo', name: 'Side information', start: p, end: siEnd,
      fields: complete ? sideInfoFields(b, p, h) : [], summary: `${si} bytes · main_data_begin ${mdb}`,
    });
    if (crcSeg) {
      // Layer III CRC covers the last two header bytes and the side information.
      const stored = u16be(b, s + 4);
      const calc = crc16Mpeg(b, p, siEnd, crc16Mpeg(b, s + 2, s + 4));
      crcSeg.fields = [field('CRC-16', `${hex(stored, 4)} (${stored === calc ? 'matches' : `expected ${hex(calc, 4)}`})`, s + 4, 2, 'A checksum over the header and side info. It lets a decoder spot damaged frames.', stored)];
      if (stored !== calc) crcSeg.warning = 'The CRC does not match. This frame may be damaged.';
    }
    p = siEnd;
    if (withMain && p < e) {
      const md: Segment = {
        kind: 'audio', type: 'mp3.maindata', name: 'Main data', start: p, end: e,
        summary: mdb ? `${fmtBytes(e - p)} · this frame's audio starts ${mdb} bytes earlier` : fmtBytes(e - p),
      };
      if (mdb && links) md.links = links;
      out.push(md);
    }
  } else {
    if (crcSeg) crcSeg.fields = [field('CRC-16', hex(u16be(b, s + 4), 4), s + 4, 2, 'A checksum over the bit allocation data. It lets a decoder spot damaged frames.')];
    if (withMain && p < e)
      out.push({ kind: 'audio', type: 'mp3.audiodata', name: 'Audio data', start: p, end: e, summary: fmtBytes(e - p) });
  }
  return out;
}

/** Main data begin (bit reservoir back-pointer) of a Layer III frame, or 0. */
function mainDataBegin(b: Uint8Array, s: number, e: number, h: number): number {
  if (hLayer(h) !== 1) return 0;
  const o = s + 4 + (hProt(h) ? 0 : 2);
  if (o + 2 > e) return 0;
  return hVer(h) === 3 ? (b[o] << 1) | (b[o + 1] >> 7) : b[o];
}
/** Where a Layer III frame's own main-data area starts (after header, CRC and side info). */
const mainDataStart = (f: Mp3Frame) => Math.min(f.end, f.start + 4 + (hProt(f.h) ? 0 : 2) + sideInfoSize(f.h));

/**
 * Links from a frame to the bytes where its audio data really begins. The bit
 * reservoir counts only main-data bytes, so we walk back through earlier frames
 * and skip their headers and side info. Returns one link per frame touched.
 */
function reservoirLinks(f: Mp3Frame): SegmentLink[] {
  const mdb = mainDataBegin(f.b, f.start, f.end, f.h);
  const out: SegmentLink[] = [];
  let need = mdb;
  let next = f.start;
  for (let i = f.index - 1; i >= 0 && need > 0; i--) {
    const g = f.list[i];
    if (g.end !== next) break; // a gap (damage or a tag) breaks the reservoir chain
    const ms = mainDataStart(g);
    const take = Math.min(need, g.end - ms);
    if (take > 0) out.push({ start: g.end - take, end: g.end, label: '' });
    need -= Math.max(0, take);
    next = g.start;
  }
  out.reverse();
  out.forEach((l, i) => {
    l.label = i === 0 ? `audio data starts ${mdb} bytes back` : `reservoir bytes (${l.end - l.start})`;
  });
  return out;
}

/** One audio frame. Everything except the byte range and time is computed on demand. */
class Mp3Frame implements Segment {
  readonly kind = 'audio' as const;
  readonly type = 'mp3.frame';
  declare warning?: string;
  constructor(
    readonly b: Uint8Array,
    readonly start: number,
    readonly end: number,
    readonly time: [number, number],
    readonly index: number,
    readonly h: number,
    /** The shared list of all audio frames (for bit-reservoir links). */
    readonly list: Mp3Frame[],
  ) {}
  get name(): string {
    return `Frame ${(this.index + 1).toLocaleString('en-US')}`;
  }
  get summary(): string {
    const h = this.h;
    let kbps = kbpsOf(h);
    if (kbps === 0) kbps = Math.round(((this.end - this.start) * 8 * sampleRateOf(h)) / samplesPerFrame(h) / 1000);
    return `${kbps} kbps · ${fmtHz(sampleRateOf(h))} · ${MODE_NAMES[hMode(h)]}`;
  }
  get fields(): () => Field[] {
    return () => (this.end - this.start >= 4 ? headerFields(this.b, this.start) : []);
  }
  get children(): () => Segment[] {
    return () => frameParts(this.b, this.start, this.end, this.h, true, () => reservoirLinks(this));
  }
  /** Layer III only: arcs to where this frame's audio data starts in earlier frames. */
  get links(): (() => SegmentLink[]) | undefined {
    return hLayer(this.h) === 1 ? () => reservoirLinks(this) : undefined;
  }
}

// ---------------------------------------------------------------------------
// Xing / Info / LAME and VBRI

interface XingInfo {
  segment: Segment;
  frames?: number;
  bytes?: number;
  isInfo: boolean; // "Info" = CBR written by LAME
  encoder?: string;
  vbrMethod?: number;
  lowpass?: number;
  delay?: number;
  padding?: number;
  musicLength?: number;
  tagCrcOk?: boolean;
}

const VBR_METHODS = ['unknown', 'CBR', 'ABR', 'VBR (old/rh)', 'VBR (mtrh)', 'VBR (mt)', 'VBR (new)', 'unknown', 'CBR (2-pass)', 'ABR (2-pass)'];

function replayGainText(v: number): string {
  const name = (v >> 13) & 7;
  if (name === 0) return 'not set';
  const orig = ['unset', 'set by artist', 'set by user', 'set automatically'][(v >> 10) & 7] ?? 'other';
  const db = ((v & 0x1ff) / 10) * (v & 0x200 ? -1 : 1);
  return `${db >= 0 ? '+' : ''}${db.toFixed(1)} dB (${orig})`;
}

/** Looks for a Xing/Info or VBRI header in the frame at [s, e). */
function parseInfoFrame(b: Uint8Array, s: number, e: number, h: number, streamEnd: () => number): XingInfo | null {
  if (hLayer(h) !== 1) return null;
  const crc = hProt(h) === 0 ? 2 : 0;
  const si = sideInfoSize(h);
  let x = -1;
  for (const cand of [s + 4 + si, s + 4 + crc + si]) if (matches(b, cand, 'Xing') || matches(b, cand, 'Info')) { x = cand; break; }
  if (x >= 0 && x + 8 <= e) return parseXing(b, s, e, h, x, streamEnd);
  const vb = s + 36;
  if (matches(b, vb, 'VBRI') && vb + 26 <= e) return parseVbri(b, s, e, h, vb);
  return null;
}

function parseXing(b: Uint8Array, s: number, e: number, h: number, x: number, streamEnd: () => number): XingInfo {
  const isInfo = matches(b, x, 'Info');
  const flags = u32be(b, x + 4);
  const xf: Field[] = [
    field('Tag ID', ascii(b, x, 4), x, 4, isInfo ? '"Info" means a constant-bitrate file (LAME writes this).' : '"Xing" means a variable-bitrate file.'),
    field('Flags', [flags & 1 ? 'frames' : '', flags & 2 ? 'bytes' : '', flags & 4 ? 'TOC' : '', flags & 8 ? 'quality' : ''].filter(Boolean).join(', ') || 'none', x + 4, 4, 'Which of the optional fields below are present.', flags),
  ];
  const info: XingInfo = { isInfo, segment: null as unknown as Segment };
  let p = x + 8;
  if (flags & 1 && p + 4 <= e) { info.frames = u32be(b, p); xf.push(field('Frame count', info.frames, p, 4, 'Number of audio frames, not counting this one. Players use it to show the length of VBR files.')); p += 4; }
  if (flags & 2 && p + 4 <= e) { info.bytes = u32be(b, p); xf.push(field('Byte count', info.bytes, p, 4, 'Size of the audio stream in bytes, including this frame.')); p += 4; }
  let toc: Segment | undefined;
  if (flags & 4 && p + 100 <= e) {
    const ts = p;
    const bytes = info.bytes;
    toc = {
      kind: 'index', type: 'mp3.xing.toc', name: 'Seek table (TOC)', start: ts, end: ts + 100, summary: '100 entries',
      fields: () => {
        const f: Field[] = [];
        for (let i = 0; i < 100; i++) {
          const v = b[ts + i];
          f.push(field(`${i}%`, bytes ? `byte ${Math.round((v / 256) * bytes).toLocaleString('en-US')} (${v})` : String(v), ts + i, 1,
            i === 0 ? 'Entry i tells where i% of the playing time starts, as a fraction (0–255) of the byte count.' : undefined, v));
        }
        return f;
      },
    };
    p += 100;
  }
  if (flags & 8 && p + 4 <= e) { xf.push(field('Quality', u32be(b, p), p, 4, 'Encoder quality indicator: 0 is best, 100 is worst.')); p += 4; }
  const xingEnd = p;
  const children: Segment[] = [...frameParts(b, s, e, h, false)];
  const si = children[children.length - 1];
  if (si.end > x) {
    // LAME puts the tag right after 32 bytes of side info even when a CRC is present,
    // so the "side info" of this frame is cut short by the tag.
    si.end = x;
    si.fields = (si.fields as Field[]).filter((f) => f.bit + f.bits <= x * 8);
    si.warning = 'The Xing tag starts inside the side information, because this frame also has a CRC.';
  }
  const firstEnd = si.end;
  if (x > firstEnd) children.push({ kind: 'padding', type: 'mp3.xing.rest', name: 'Unused', start: firstEnd, end: x });
  children.push({ kind: 'header', type: 'mp3.xing.tag', name: isInfo ? 'Info tag' : 'Xing tag', start: x, end: xingEnd, fields: xf, children: toc ? [toc] : [] });
  p = xingEnd;

  // LAME extension: 36 bytes that start with a 9-byte encoder string.
  const vendor = ascii(b, p, 9);
  if (p + 36 <= e && /^[A-Za-z]{4}[\x20-\x7e]/.test(vendor)) {
    const L = p;
    const enc = vendor.replace(/\0.*$/, '').trim();
    info.encoder = enc.replace(/^LAME(?=\d)/, 'LAME ');
    info.vbrMethod = b[L + 9] & 15;
    info.lowpass = b[L + 10] * 100;
    info.delay = (b[L + 21] << 4) | (b[L + 22] >> 4);
    info.padding = ((b[L + 22] & 15) << 8) | b[L + 23];
    info.musicLength = u32be(b, L + 28);
    const tagCrc = u16be(b, L + 34);
    const calcTagCrc = crc16Arc(b, s, L + 34);
    info.tagCrcOk = tagCrc === calcTagCrc;
    const lf: Field[] = [
      field('Encoder', vendor.replace(/\0/g, ' ').trimEnd(), L, 9, 'The encoder name and version.'),
    ];
    const bf = new BitFields(b, L + 9);
    bf.read('Tag revision', 4, undefined, 'Version of the LAME tag format.');
    bf.read('VBR method', 4, (v) => VBR_METHODS[v] ?? 'unknown', 'How the encoder picked bitrates: fixed (CBR), average (ABR) or by quality (VBR).');
    bf.read('Lowpass', 8, (v) => (v ? `${fmtHz(v * 100)}` : 'none'), 'The encoder removed all sound above this frequency, to spend bits on sounds people hear.');
    lf.push(...bf.fields);
    const peak = u32be(b, L + 11);
    lf.push(field('Peak amplitude', peak ? (peak / 8388608).toFixed(6) : 'not set', L + 11, 4, 'The loudest sample, where 1.0 is full scale.', peak));
    lf.push(field('Track gain', replayGainText(u16be(b, L + 15)), L + 15, 2, 'ReplayGain for this track: how much to change the volume so tracks play at even loudness.'));
    lf.push(field('Album gain', replayGainText(u16be(b, L + 17)), L + 17, 2, 'ReplayGain for the whole album.'));
    const bf2 = new BitFields(b, L + 19);
    bf2.read('Encoding flags', 4, (v) => [v & 1 ? 'nspsytune' : '', v & 2 ? 'nssafejoint' : '', v & 4 ? 'nogap (next)' : '', v & 8 ? 'nogap (prev)' : ''].filter(Boolean).join(', ') || 'none', 'Encoder switches that were on.');
    bf2.read('ATH type', 4, undefined, 'Which model of the quietest sound people can hear the encoder used.');
    const m = info.vbrMethod;
    bf2.read(m === 2 || m === 9 ? 'ABR target bitrate' : 'Minimum bitrate', 8, (v) => (v === 255 ? '255+ kbps' : `${v} kbps`), 'For ABR the target bitrate; otherwise the lowest bitrate allowed.');
    bf2.read('Encoder delay', 12, (v) => `${v} samples`, 'Silent samples the encoder added at the start. Gapless players skip them.');
    bf2.read('Encoder padding', 12, (v) => `${v} samples`, 'Silent samples added at the end to fill the last frame. Gapless players skip them.');
    bf2.read('Source sample rate', 2, (v) => ['≤ 32 kHz', '44.1 kHz', '48 kHz', '> 48 kHz'][v], 'Sample rate of the original input.');
    bf2.read('Unwise settings', 1, (v) => (v ? 'yes' : 'no'), 'Set when the user picked options that can hurt quality.');
    bf2.read('Stereo mode', 3, (v) => ['Mono', 'Stereo', 'Dual', 'Joint', 'Force', 'Auto', 'Intensity', 'Undefined'][v], 'The stereo mode the user asked for.');
    bf2.read('Noise shaping', 2, undefined, 'Noise shaping mode used by the encoder.');
    bf2.read('MP3 gain', 8, (v) => `${((v << 24) >> 24) * 1.5} dB`, 'Volume change applied by mp3gain, in 1.5 dB steps.');
    bf2.read('Unused', 2);
    bf2.read('Surround', 3, (v) => ['none', 'DPL', 'DPL2', 'Ambisonic'][v] ?? 'reserved', 'Surround encoding info.');
    bf2.read('Preset', 11, (v) => (v >= 410 && v <= 500 && v % 10 === 0 ? `V${(500 - v) / 10} (${v})` : v === 0 ? 'none' : String(v)), 'The LAME preset. 480 means -V 2.');
    lf.push(...bf2.fields);
    lf.push(field('Music length', info.musicLength, L + 28, 4, 'Bytes of audio including this frame, as written by the encoder.'));
    const musicCrc = u16be(b, L + 32);
    lf.push({
      ...field('Music CRC', hex(musicCrc, 4), L + 32, 2, 'CRC-16 of all audio frames after this one. It shows whether the audio was changed.', musicCrc),
    });
    lf.push(field('Tag CRC', `${hex(tagCrc, 4)} (${info.tagCrcOk ? 'matches' : `expected ${hex(calcTagCrc, 4)}`})`, L + 34, 2, 'CRC-16 of the first 190 bytes of this frame. It protects the tag itself.', tagCrc));
    // Checking the music CRC reads every audio byte, so do it only when asked.
    const lame: Segment = {
      kind: 'header', type: 'mp3.lame', name: 'LAME tag', start: L, end: L + 36, summary: info.encoder,
      fields: () => {
        // Music length counts from the start of this frame; use it so a joined file
        // only checks its own part.
        const ml = info.musicLength ?? 0;
        const endByte = Math.min(streamEnd(), ml > e - s ? s + ml : Infinity);
        const calc = crc16Arc(b, e, endByte);
        const f = lf.slice();
        const i = f.findIndex((x) => x.name === 'Music CRC');
        f[i] = { ...f[i], value: `${hex(musicCrc, 4)} (${calc === musicCrc ? 'matches' : `now ${hex(calc, 4)}: audio was changed or cut`})` };
        return f;
      },
    };
    if (!info.tagCrcOk) lame.warning = 'The LAME tag CRC does not match. Some tool changed this frame after encoding.';
    children.push(lame);
    p = L + 36;
  }
  if (p < e) children.push({ kind: 'padding', type: 'mp3.xing.rest', name: 'Unused', start: p, end: e, summary: fmtBytes(e - p) });
  info.segment = {
    kind: 'header', type: 'mp3.xing', name: isInfo ? 'Info frame' : 'Xing frame', start: s, end: e, children,
    summary: [info.frames !== undefined ? plural(info.frames, 'frame') : '', info.encoder ?? ''].filter(Boolean).join(' · '),
    fields: xf,
  };
  return info;
}

function parseVbri(b: Uint8Array, s: number, e: number, h: number, v: number): XingInfo {
  const entries = u16be(b, v + 18), scale = u16be(b, v + 20), entrySize = u16be(b, v + 22), perEntry = u16be(b, v + 24);
  const vf: Field[] = [
    field('Tag ID', 'VBRI', v, 4, 'Marks a VBRI header, written by the Fraunhofer encoder.'),
    field('Version', u16be(b, v + 4), v + 4, 2),
    field('Delay', u16be(b, v + 6), v + 6, 2, 'Encoder delay (a fixed-point number).'),
    field('Quality', u16be(b, v + 8), v + 8, 2, 'Encoder quality setting.'),
    field('Byte count', u32be(b, v + 10), v + 10, 4, 'Size of the audio stream in bytes.'),
    field('Frame count', u32be(b, v + 14), v + 14, 4, 'Number of audio frames. Players use it to show the length.'),
    field('TOC entries', entries, v + 18, 2, 'Number of entries in the seek table.'),
    field('TOC scale', scale, v + 20, 2, 'Each entry is multiplied by this.'),
    field('Entry size', `${entrySize} bytes`, v + 22, 2, 'Bytes per seek-table entry.', entrySize),
    field('Frames per entry', perEntry, v + 24, 2, 'How many frames each entry covers.'),
  ];
  const children = frameParts(b, s, e, h, false);
  const last = children[children.length - 1].end;
  if (v > last) children.push({ kind: 'padding', type: 'mp3.xing.rest', name: 'Unused', start: last, end: v });
  const tocEnd = Math.min(e, v + 26 + entries * entrySize);
  const tag: Segment = { kind: 'header', type: 'mp3.vbri', name: 'VBRI tag', start: v, end: tocEnd, fields: vf };
  if (tocEnd > v + 26) tag.children = [{ kind: 'index', type: 'mp3.xing.toc', name: 'Seek table (TOC)', start: v + 26, end: tocEnd, summary: plural(entries, 'entry').replace('entrys', 'entries') }];
  children.push(tag);
  if (tocEnd < e) children.push({ kind: 'padding', type: 'mp3.xing.rest', name: 'Unused', start: tocEnd, end: e });
  const frames = u32be(b, v + 14);
  return {
    isInfo: false, frames, bytes: u32be(b, v + 10),
    segment: { kind: 'header', type: 'mp3.vbri', name: 'VBRI frame', start: s, end: e, children, fields: vf, summary: plural(frames, 'frame') },
  };
}

// ---------------------------------------------------------------------------
// Trailing tags: APEv2, Lyrics3, ID3v1 (shared with the ADTS parser)

const APE_KEYS: Record<string, string> = {
  title: 'Title', artist: 'Artist', album: 'Album', 'album artist': 'Album artist', albumartist: 'Album artist', year: 'Year',
  track: 'Track', genre: 'Genre', comment: 'Comment', composer: 'Composer', disc: 'Disc', copyright: 'Copyright',
  replaygain_track_gain: 'ReplayGain (track)', replaygain_album_gain: 'ReplayGain (album)', lyrics: 'Lyrics',
};

function sniffMime(b: Uint8Array, o: number): string {
  if (b[o] === 0xff && b[o + 1] === 0xd8) return 'image/jpeg';
  if (matches(b, o + 1, 'PNG')) return 'image/png';
  if (matches(b, o, 'GIF8')) return 'image/gif';
  if (matches(b, o + 8, 'WEBP')) return 'image/webp';
  return 'application/octet-stream';
}

/** Parses an APEv1/v2 tag whose 32-byte footer starts at `f`. Returns null if it is not valid. */
export function parseApeTag(b: Uint8Array, f: number, lo: number): { segment: Segment; tags: Tag[]; pictures: Picture[] } | null {
  if (!matches(b, f, 'APETAGEX') || f + 32 > b.length) return null;
  const version = u32le(b, f + 8), size = u32le(b, f + 12), count = u32le(b, f + 16), flags = u32le(b, f + 20);
  const hasHeader = !!(flags & 0x80000000) && version >= 2000;
  const itemsStart = f + 32 - size;
  const start = itemsStart - (hasHeader ? 32 : 0);
  if (size < 32 || start < lo) return null;
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  const hf = (o: number): Field[] => [
    field('Preamble', 'APETAGEX', o, 8, 'Marks an APE tag header or footer.'),
    field('Version', version === 2000 ? '2.0' : version === 1000 ? '1.0' : String(version), o + 8, 4, 'APE tag version (1000 or 2000).', version),
    field('Tag size', size, o + 12, 4, 'Size of all items plus the footer, in bytes (little-endian).'),
    field('Item count', count, o + 16, 4, 'How many items the tag holds.'),
    field('Flags', hex(u32le(b, o + 20), 8), o + 20, 4, 'Bit 31: a header exists. Bit 29: this is the header.'),
    field('Reserved', '0', o + 24, 8, 'Must be zero.'),
  ];
  const children: Segment[] = [];
  if (hasHeader) children.push({ kind: 'header', type: 'ape.header', name: 'APE header', start, end: itemsStart, fields: hf(start) });
  let p = itemsStart;
  for (let i = 0; i < count && p + 8 < f; i++) {
    const len = u32le(b, p), iflags = u32le(b, p + 4);
    let k = p + 8;
    while (k < f && b[k] !== 0) k++;
    const key = ascii(b, p + 8, k - p - 8);
    const vs = k + 1, ve = vs + len;
    if (ve > f) break;
    const kind = (iflags >> 1) & 3;
    const fields: Field[] = [
      field('Value size', len, p, 4, 'Length of the value in bytes.'),
      field('Item flags', ['UTF-8 text', 'binary', 'link', 'reserved'][kind], p + 4, 4, 'Says whether the value is text, binary data or a link.', iflags),
      field('Key', key, p + 8, vs - p - 8, 'The item name, ASCII, ended by a zero byte. Case does not matter.'),
    ];
    const seg: Segment = { kind: 'metadata', type: 'ape.item', name: key || '(no key)', start: p, end: ve, fields };
    if (kind === 1 && /^cover art/i.test(key)) {
      let q = vs;
      while (q < ve && b[q] !== 0) q++;
      const img = Math.min(ve, q + 1);
      const mime = sniffMime(b, img);
      fields.push(field('Filename', ascii(b, vs, q - vs) || '(empty)', vs, img - vs, 'The original name of the image file.'));
      if (ve > img) {
        fields.push(field('Image data', fmtBytes(ve - img), img, ve - img, 'The raw image file.'));
        seg.children = [{ kind: 'image', type: 'ape.image', name: `${mime} data`, start: img, end: ve, image: { mime, start: img, end: ve }, summary: fmtBytes(ve - img) }];
        pictures.push({ mime, start: img, end: ve, pictureType: key.replace(/^cover art\s*/i, '').replace(/[()]/g, '') || undefined });
      }
      seg.summary = `${mime} · ${fmtBytes(ve - img)}`;
    } else if (len > 0) {
      const value = kind === 1 ? `${len} bytes` : utf8Str(b, vs, Math.min(len, 4096)).replace(/\0/g, ' / ');
      fields.push(field('Value', value, vs, len, kind === 0 ? 'The value as UTF-8 text.' : 'The item value.'));
      seg.summary = value.length > 80 ? value.slice(0, 80) + '…' : value;
      if (kind !== 1) tags.push({ key: APE_KEYS[key.toLowerCase()] ?? key, value, rawKey: key });
    }
    children.push(seg);
    p = ve;
  }
  if (p < f) children.push({ kind: 'unknown', type: 'ape.item', name: 'Unparsed items', start: p, end: f, warning: 'These bytes do not form valid APE items.' });
  children.push({ kind: 'header', type: 'ape.footer', name: 'APE footer', start: f, end: f + 32, fields: hf(f) });
  return {
    segment: { kind: 'metadata', type: 'ape.tag', name: `APEv${version >= 2000 ? 2 : 1} tag`, start, end: f + 32, children, summary: plural(count, 'item') },
    tags, pictures,
  };
}

export interface TrailingTags {
  segments: Segment[];
  tags: Tag[];
  pictures: Picture[];
  /** Offset where the trailing tags begin (the end of the audio). */
  start: number;
}

/** Finds ID3v1, APEv2 and Lyrics3 tags at the end of the file, not below `lo`. */
export function parseTrailingTags(b: Uint8Array, lo: number): TrailingTags {
  const segs: Segment[] = [];
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  let end = b.length;
  for (let guard = 0; guard < 8; guard++) {
    if (end - 32 >= lo && matches(b, end - 32, 'APETAGEX')) {
      const ape = parseApeTag(b, end - 32, lo);
      if (ape) { segs.push(ape.segment); tags.push(...ape.tags); pictures.push(...ape.pictures); end = ape.segment.start; continue; }
    }
    // "TAG" inside an APE preamble ("APETAGEX") is not an ID3v1 tag.
    if (end - 128 >= lo && !matches(b, end - 131, 'APETAGEX')) {
      const v1 = parseId3v1(b, end - 128);
      if (v1) { segs.push(v1.segment); tags.push(...v1.tags); end -= 128; continue; }
    }
    if (end - 15 >= lo && matches(b, end - 9, 'LYRICS200')) {
      const n = parseInt(ascii(b, end - 15, 6), 10);
      const s = end - 15 - n;
      if (n > 0 && s >= lo && matches(b, s, 'LYRICSBEGIN')) {
        segs.push(lyrics3(b, s, end, 2));
        end = s;
        continue;
      }
    }
    if (end - 20 >= lo && matches(b, end - 9, 'LYRICSEND')) {
      // Lyrics3 v1: at most 5100 bytes of lyrics between the markers.
      const from = Math.max(lo, end - 9 - 5100 - 11);
      const text = ascii(b, from, end - from);
      const i = text.lastIndexOf('LYRICSBEGIN');
      if (i >= 0) { segs.push(lyrics3(b, from + i, end, 1)); end = from + i; continue; }
    }
    break;
  }
  segs.reverse();
  return { segments: segs, tags, pictures, start: end };
}

function lyrics3(b: Uint8Array, s: number, e: number, ver: number): Segment {
  const fields: Field[] = [field('Begin marker', 'LYRICSBEGIN', s, 11, 'Marks the start of a Lyrics3 block.')];
  if (ver === 2) {
    // Lyrics3v2 fields: 3-letter ID, 5-digit size, data.
    let p = s + 11;
    while (p + 8 <= e - 15) {
      const id = ascii(b, p, 3), n = parseInt(ascii(b, p + 3, 5), 10);
      if (!/^[A-Z0-9]{3}$/.test(id) || !(n >= 0) || p + 8 + n > e - 15) break;
      fields.push(field(id, ascii(b, p + 8, Math.min(n, 200)) + (n > 200 ? '…' : ''), p, 8 + n, ({ IND: 'Indicators', LYR: 'Lyrics', INF: 'Info', AUT: 'Author', EAL: 'Album', EAR: 'Artist', ETT: 'Title', IMG: 'Image link' } as Record<string, string>)[id]));
      p += 8 + n;
    }
    fields.push(field('Size', parseInt(ascii(b, e - 15, 6), 10), e - 15, 6, 'Size of the block before this field, as 6 ASCII digits.'));
    fields.push(field('End marker', 'LYRICS200', e - 9, 9, 'Marks the end of a Lyrics3 v2 block.'));
  } else {
    fields.push(field('Lyrics', ascii(b, s + 11, Math.min(200, e - 20 - s)), s + 11, e - 9 - s - 11, 'The lyrics as plain text.'));
    fields.push(field('End marker', 'LYRICSEND', e - 9, 9, 'Marks the end of a Lyrics3 v1 block.'));
  }
  return { kind: 'metadata', type: 'lyrics3', name: `Lyrics3 v${ver}`, start: s, end: e, fields, summary: fmtBytes(e - s) };
}

// ---------------------------------------------------------------------------
// Sync search

/** True when a valid frame at `p` is followed by `need` more frames with the same stream key. */
function chainAt(b: Uint8Array, p: number, end: number, need: number, key: number | null): boolean {
  let h = u32be(b, p);
  if (!validHeader(h) || (key !== null && streamKey(h) !== key)) return false;
  const k = streamKey(h);
  let free = 0;
  if (hBr(h) === 0) free = freeFormatSlots(b, p, end, h);
  for (let i = 0; i < need; i++) {
    const len = frameLength(h, free);
    if (len < 4) return false;
    const q = p + len;
    if (q === end || (q > end && i > 0)) return true;
    if (q + 4 > end) return i > 0 && q <= end;
    h = u32be(b, q);
    if (!validHeader(h) || streamKey(h) !== k) return false;
    p = q;
  }
  return true;
}

/** For a free-format frame at p: finds the next matching header and returns the slot count (without padding). */
function freeFormatSlots(b: Uint8Array, p: number, end: number, h: number): number {
  const mask = 0xfffefe00 >>> 0; // same sync, version, layer, bitrate index 0, sample rate; ignore padding
  const want = (h & mask) >>> 0;
  const lim = Math.min(end - 4, p + 8000);
  for (let q = p + 20; q <= lim; q++) {
    if (b[q] === 0xff && ((u32be(b, q) & mask) >>> 0) === want) {
      const slots = q - p;
      return hLayer(h) === 3 ? slots - hPad(h) * 4 : slots - hPad(h);
    }
  }
  return 0;
}

const looksLikeId3 = (b: Uint8Array, p: number) => matches(b, p, 'ID3') && b[p + 3] >= 2 && b[p + 3] <= 4 && !((b[p + 6] | b[p + 7] | b[p + 8] | b[p + 9]) & 0x80);

/** Finds the next offset in [from, end) where a trustworthy frame chain (or an ID3v2 tag) starts. */
function findSync(b: Uint8Array, from: number, end: number, key: number | null, need: number): number {
  for (let i = from; i + 4 <= end; i++) {
    const c = b[i];
    if (c === 0xff) {
      if ((b[i + 1] & 0xe0) === 0xe0 && chainAt(b, i, end, need, key)) return i;
    } else if (c === 0x49 && looksLikeId3(b, i)) return i;
  }
  return end;
}

// ---------------------------------------------------------------------------
// Parser

function skipId3(b: Uint8Array, p: number): number {
  for (let guard = 0; guard < 16 && looksLikeId3(b, p); guard++) {
    const footer = b[p + 3] === 4 && b[p + 5] & 0x10 ? 10 : 0;
    p += 10 + (((b[p + 6] & 0x7f) << 21) | ((b[p + 7] & 0x7f) << 14) | ((b[p + 8] & 0x7f) << 7) | (b[p + 9] & 0x7f)) + footer;
  }
  return p;
}

function detect(b: Uint8Array): boolean {
  const p = skipId3(b, 0);
  if (p + 4 > b.length) return false;
  for (const magic of ['fLaC', 'RIFF', 'FORM', 'OggS', 'APETAGEX']) if (matches(b, p, magic)) return false;
  if (matches(b, 4, 'ftyp')) return false;
  const end = b.length;
  if (b[p] === 0xff && chainAt(b, p, end, 2, null)) return true;
  // Allow some junk before the first frame, but then ask for a longer chain.
  const lim = Math.min(end, p + (p > 0 ? 65536 : 8192));
  for (let i = p + 1; i + 4 <= lim; i++) if (b[i] === 0xff && (b[i + 1] & 0xe0) === 0xe0 && chainAt(b, i, end, 4, null)) return true;
  return false;
}

function parse(b: Uint8Array): ParsedFile {
  const size = b.length;
  const tags: Tag[] = [];
  const pictures: Picture[] = [];
  const notes: Note[] = [];
  const top: Segment[] = [];
  let tagBytes = 0;
  let imageBytes = 0;

  // Leading ID3v2 tags (some files have more than one).
  let pos = 0;
  for (let guard = 0; guard < 16; guard++) {
    const r = parseId3v2(b, pos);
    if (!r || r.end <= pos) break;
    top.push(r.segment);
    tags.push(...r.tags);
    pictures.push(...r.pictures);
    notes.push(...r.notes);
    tagBytes += r.end - pos;
    pos = r.end;
  }
  if (top.length > 1) notes.push({ level: 'warn', text: `The file starts with ${top.length} ID3v2 tags in a row. Most players read only the first one.` });

  const tail = parseTrailingTags(b, pos);
  const end = tail.start;
  for (const s of tail.segments) tagBytes += s.end - s.start;
  tags.push(...tail.tags);
  pictures.push(...tail.pictures);
  for (const p of pictures) imageBytes += p.end - p.start;

  // Find the first frame. Ask for a chain of frames so random 0xFF bytes in junk do not fool us.
  let first = pos + 4 <= end && b[pos] === 0xff && chainAt(b, pos, end, 2, null) ? pos : findSync(b, pos, end, null, 3);
  if (first >= end) first = findSync(b, pos, end, null, 1); // tiny files: accept a single frame
  if (first > pos) {
    const allZero = b.subarray(pos, first).every((x) => x === 0);
    top.push({
      kind: allZero ? 'padding' : 'unknown', type: 'mp3.junk', name: allZero ? 'Zero bytes' : 'Junk before audio', start: pos, end: first,
      summary: fmtBytes(first - pos), warning: first < end ? `${fmtBytes(first - pos)} of ${allZero ? 'zero bytes' : 'unknown data'} come before the first MPEG frame. Players skip them by searching for a frame sync.` : 'No MPEG audio frames were found in these bytes.',
    });
    notes.push({ level: 'warn', text: first < end ? `Found ${fmtBytes(first - pos)} of ${allZero ? 'zero bytes' : 'unknown data'} before the first audio frame. Players must search past it, which can confuse format detection.` : 'No valid MPEG audio frames were found.' });
  }

  // Scan frames.
  const items: Segment[] = []; // everything from the first frame to the end of the audio
  const frames: Mp3Frame[] = [];
  let ref = first < end ? streamKey(u32be(b, first)) : 0;
  let refHeader = first < end ? u32be(b, first) : 0;
  let free = hBr(refHeader) === 0 && first < end ? freeFormatSlots(b, first, end, refHeader) : 0;
  const sr = first < end ? sampleRateOf(refHeader) : 0;
  const spf = first < end ? samplesPerFrame(refHeader) : 0;
  let samples = 0;
  let expectInfo = true;
  let xing: XingInfo | null = null;
  let audioBytes = 0;
  let minKbps = Infinity, maxKbps = 0;
  let reservoirFrames = 0, layer3Frames = 0;
  let badSpans = 0, badBytes = 0;
  let lastFrameEnd = first;
  const modeCount = [0, 0, 0, 0];
  let p = first;
  while (p < end) {
    let accepted = false;
    if (p + 4 <= end) {
      const h = u32be(b, p);
      if (validHeader(h) && streamKey(h) === ref) {
        const len = frameLength(h, free);
        const q = p + len;
        if (len >= 4 + (hProt(h) ? 0 : 2) && (q >= end || (q + 4 <= end && b[q] === 0xff && validHeader(u32be(b, q)) && streamKey(u32be(b, q)) === ref) || looksLikeId3(b, q) || q + 4 > end)) {
          accepted = true;
          const fe = Math.min(q, end);
          const info = expectInfo ? parseInfoFrame(b, p, fe, h, () => lastFrameEnd) : null;
          expectInfo = false;
          if (info) {
            if (!xing) xing = info;
            items.push(info.segment);
          } else {
            const t0 = samples / sr;
            samples += spf;
            const f = new Mp3Frame(b, p, fe, [t0, samples / sr], frames.length, h, frames);
            if (q > end) f.warning = `This frame should be ${len} bytes, but only ${end - p} remain. The file is cut short.`;
            frames.push(f);
            items.push(f);
            audioBytes += fe - p;
            const kbps = kbpsOf(h) || Math.round((len * 8 * sr) / spf / 1000);
            if (kbps < minKbps) minKbps = kbps;
            if (kbps > maxKbps) maxKbps = kbps;
            modeCount[hMode(h)]++;
            if (hLayer(h) === 1) {
              layer3Frames++;
              if (mainDataBegin(b, p, fe, h) > 0) reservoirFrames++;
            }
          }
          lastFrameEnd = fe;
          p = fe;
        }
      }
    }
    if (accepted) continue;
    // An ID3v2 tag in the middle of the stream (for example two files joined together).
    if (looksLikeId3(b, p)) {
      const r = parseId3v2(b, p);
      if (r && r.end > p) {
        const seg = r.segment;
        seg.warning = 'An ID3v2 tag in the middle of the audio. This often means two MP3 files were joined together.';
        items.push(seg);
        tagBytes += r.end - p;
        p = Math.min(r.end, end);
        expectInfo = true;
        continue;
      }
    }
    // Lost sync: find the next trustworthy frame.
    let q = findSync(b, p + 1, end, ref, 2);
    if (q >= end) {
      // Maybe the stream changed format (joined files); accept any consistent stream.
      const q2 = findSync(b, p + 1, end, null, 3);
      if (q2 < end) {
        q = q2;
        if (!looksLikeId3(b, q)) {
          refHeader = u32be(b, q);
          if (sampleRateOf(refHeader) === sr && samplesPerFrame(refHeader) === spf) ref = streamKey(refHeader);
          else q = end; // a different sample rate would break the time axis; treat the rest as unknown
          free = hBr(refHeader) === 0 ? freeFormatSlots(b, q, end, refHeader) : 0;
        }
      }
    }
    const allZero = b.subarray(p, q).every((x) => x === 0);
    items.push({
      kind: allZero ? 'padding' : 'unknown', type: 'mp3.garbage', name: allZero ? 'Zero bytes' : 'Unparsed bytes', start: p, end: q, summary: fmtBytes(q - p),
      warning: allZero ? 'Zero bytes between or after frames.' : `These ${fmtBytes(q - p)} are not valid MPEG frames. A player skips them and searches for the next frame sync.`,
    });
    if (!allZero) { badSpans++; badBytes += q - p; }
    p = q;
  }

  // Group the audio frames so the tree stays small; keep header frames and trailing junk outside.
  let gi = items.findIndex((s) => s instanceof Mp3Frame);
  let gj = items.length - 1;
  while (gj >= 0 && !(items[gj] instanceof Mp3Frame)) gj--;
  if (gi < 0) {
    top.push(...items);
  } else {
    top.push(...items.slice(0, gi));
    const inner = items.slice(gi, gj + 1);
    const dur = samples / sr;
    top.push({
      kind: 'audio', type: 'mp3.frames', name: 'Audio frames', start: inner[0].start, end: inner[inner.length - 1].end,
      summary: `${plural(frames.length, 'frame')} · ${fmtSec(dur)}`, children: inner, time: [0, dur],
    });
    top.push(...items.slice(gj + 1));
  }
  top.push(...tail.segments);

  // Audio info.
  const h0 = frames.length ? frames[0].h : refHeader;
  const layer = hLayer(h0);
  const ver = hVer(h0);
  const codec = layer === 1 ? 'MP3' : layer === 2 ? 'MP2' : 'MP1';
  const channels = hMode(h0) === 3 ? 1 : 2;
  const rawDuration = sr ? samples / sr : 0;
  const delay = xing?.delay, padding = xing?.padding;
  let totalSamples = samples;
  if (delay !== undefined && padding !== undefined && delay + padding < samples) totalSamples = samples - delay - padding;
  const cbr = frames.length > 0 && minKbps === maxKbps;
  const bitrateMode: 'CBR' | 'VBR' | 'ABR' = cbr ? 'CBR' : xing?.vbrMethod === 2 || xing?.vbrMethod === 9 ? 'ABR' : 'VBR';
  const avgBitrate = rawDuration > 0 ? Math.round((audioBytes * 8) / rawDuration) : 0;
  let encoder = xing?.encoder;
  if (!encoder) encoder = tags.find((t) => t.rawKey === 'TSSE' || t.rawKey === 'TENC')?.value;
  // Most common channel mode wins (joint stereo files can contain some plain stereo frames).
  const mode = modeCount.indexOf(Math.max(...modeCount));

  const audio = {
    codec,
    codecLong: `${VERSION_NAMES[ver]} Audio ${LAYER_NAMES[layer]}`,
    sampleRate: sr || undefined,
    channels,
    channelMode: MODE_NAMES[frames.length ? mode : hMode(h0)],
    duration: sr ? totalSamples / sr : undefined,
    totalSamples: sr ? totalSamples : undefined,
    bitrate: avgBitrate || undefined,
    bitrateMode: frames.length ? bitrateMode : undefined,
    lossless: false,
    encoder,
    encoderDelay: delay,
    encoderPadding: padding,
  };

  // Observations.
  if (frames.length) {
    if (cbr) notes.push({ level: 'info', text: `Constant bitrate (CBR): every frame is ${minKbps} kbps. Frame sizes are fixed, so players can seek with simple math.` });
    else notes.push({ level: 'info', text: `${bitrateMode === 'ABR' ? 'Average' : 'Variable'} bitrate (${bitrateMode}): frames range from ${minKbps} to ${maxKbps} kbps, ${fmtKbps(avgBitrate)} on average. Simple passages get fewer bits, complex ones get more.` });
    if (!cbr && !xing) notes.push({ level: 'warn', text: 'This VBR file has no Xing or VBRI header. Players must scan the whole file to know its length, and seeking may be off.' });
    if (xing?.frames !== undefined && xing.frames !== frames.length)
      notes.push({ level: 'warn', text: `The ${xing.isInfo ? 'Info' : 'Xing'} header says ${xing.frames.toLocaleString('en-US')} frames, but the file has ${frames.length.toLocaleString('en-US')}. The file was probably cut or edited after encoding.` });
    if (layer3Frames > 0 && reservoirFrames > 0)
      notes.push({ level: 'info', text: `${reservoirFrames === layer3Frames ? 'All' : Math.floor((reservoirFrames / layer3Frames) * 100) + '%'} of frames use the bit reservoir: their audio data starts in an earlier frame. Easy frames leave spare bytes, and hard frames borrow them.` });
    if (delay !== undefined && padding !== undefined)
      notes.push({ level: 'info', text: `The encoder added ${delay} samples of silence at the start and ${padding.toLocaleString('en-US')} at the end (${((delay + padding) / sr * 1000).toFixed(0)} ms in all). Gapless players read the LAME tag and remove them.` });
    else if (layer === 1) notes.push({ level: 'info', text: 'There is no LAME tag, so players cannot know the encoder delay. Expect a short silence at the start (usually about 1,105 samples).' });
    if (xing?.lowpass) notes.push({ level: 'info', text: `The encoder removed all sound above ${fmtHz(xing.lowpass)} (lowpass). Few people hear that high, so the bits go where they matter.` });
    if (xing && xing.tagCrcOk === false) notes.push({ level: 'warn', text: 'The LAME tag checksum does not match. A tool changed the header frame after encoding.' });
    if (ver !== 3) notes.push({ level: 'info', text: `${VERSION_NAMES[ver]} uses half-size frames (${spf} samples) for low sample rates like ${fmtHz(sr)}.` });
  }
  if (badSpans) notes.push({ level: 'warn', text: `Found ${plural(badSpans, 'damaged or unknown span')} (${fmtBytes(badBytes)}) between frames. Players skip such bytes, but you may hear a glitch.` });
  if (tagBytes > 0 && size > 0) {
    const pct = (tagBytes / size) * 100;
    notes.push({ level: 'info', text: `Tags take ${fmtBytes(tagBytes)} (${pct < 0.1 ? 'under 0.1' : pct < 1 ? pct.toFixed(1) : Math.round(pct)}% of the file)${imageBytes > tagBytes / 2 ? ', mostly cover art' : ''}.` });
  }

  return {
    format: layer === 2 ? 'mp2' : 'mp3',
    container: layer === 2 ? 'MP2' : layer === 3 ? 'MP1' : 'MP3',
    size, segments: top, frames, audio, tags, pictures, notes,
  };
}

// ---------------------------------------------------------------------------
// Explanations

/** Explanations for the trailing tags (APE, Lyrics3). The ADTS parser shares them. */
export const trailingTagExplanations: Record<string, Explanation> = {
  'ape.tag': {
    title: 'APE tag',
    body: 'A tag format from Monkey\'s Audio, often added to MP3s by tools like foobar2000 and mp3gain. It sits at the end of the file and stores any key/value pair as UTF-8 text.',
  },
  'ape.header': { title: 'APE tag header', body: 'An optional 32-byte header at the start of an APEv2 tag. It is a copy of the footer, so a reader can find the tag from either end.' },
  'ape.footer': { title: 'APE tag footer', body: 'The 32 bytes at the end of an APE tag, starting with `APETAGEX`. Readers look for it at the end of the file, then use its size to find the items.' },
  'ape.item': { title: 'APE tag item', body: 'One tag: a value size, flags, a key name ended by a zero byte, then the value. The flags say if the value is text, binary data, or a link.' },
  'ape.image': { title: 'Image data', body: 'Cover art stored in an APE tag item. A filename comes first, then the raw image bytes.' },
  lyrics3: { title: 'Lyrics3 block', body: 'Song lyrics stored between the audio and the ID3v1 tag. It was made in the 1990s so players that knew only ID3v1 could still find lyrics. It is rare today.' },
};

const mp3Explanations: Record<string, Explanation> = {
  'mp3.frames': {
    title: 'Audio frames',
    body: 'The audio itself: a long run of frames, one after another. Each frame holds a fixed number of samples (1152 for MP3 at 44.1 kHz, about 26 ms). There is no file header, so a player can start reading at any frame.',
  },
  'mp3.frame': {
    title: 'MPEG audio frame',
    body: 'One small, self-contained block of compressed sound. It starts with a 4-byte header that gives the bitrate, sample rate and channel mode. Frames are the reason you can cut an MP3 anywhere and it still plays.',
  },
  'mp3.header': {
    title: 'Frame header',
    body: 'Thirty-two packed bits at the start of every frame. The first 11 bits are all 1 (the **sync word**), so a player can find the next frame even after damage. The other bits give everything needed to decode the frame and find its length.',
  },
  'mp3.crc': {
    title: 'CRC checksum',
    body: 'An optional 16-bit checksum right after the header. It lets a decoder notice damaged frames, for example on a broadcast link. Most files leave it out to save 2 bytes per frame.',
  },
  'mp3.sideinfo': {
    title: 'Side information',
    body: 'Tells the decoder how to read the main data: how many bits each part uses, the loudness step (global gain), and the window type. The field `main_data_begin` points back into earlier frames. This is the **bit reservoir**: easy frames leave spare bytes, and hard frames use them.',
  },
  'mp3.maindata': {
    title: 'Main data',
    body: 'The compressed sound: scale factors and Huffman-coded frequency values. Because of the bit reservoir, these bytes can belong to this frame or to the next frames. The decoder turns them into 576 frequency lines per granule, then back into samples.',
  },
  'mp3.audiodata': {
    title: 'Audio data',
    body: 'The compressed sound for Layer I or II: bit allocation, scale factors, then quantized samples for 32 frequency sub-bands. Layers I and II have no bit reservoir, so every frame holds only its own data.',
  },
  'mp3.xing': {
    title: 'Xing / Info header frame',
    body: 'The first frame looks like audio but plays as silence. Encoders put a summary of the whole file here: the frame count, byte count and a seek table. Without it, a player must read the whole VBR file to know its length.',
  },
  'mp3.xing.tag': {
    title: 'Xing tag',
    body: 'The summary fields, starting with `Xing` (variable bitrate) or `Info` (constant bitrate). A flags word says which fields follow: frame count, byte count, seek table, and quality.',
  },
  'mp3.xing.toc': {
    title: 'Seek table (TOC)',
    body: 'A table of 100 byte positions, one for each percent of the playing time. In a VBR file, time and bytes do not grow at the same rate, so a player uses this table to jump to 50% of the song.',
  },
  'mp3.lame': {
    title: 'LAME tag',
    body: 'Extra facts the LAME encoder adds after the Xing tag. The most useful are the **encoder delay** and **padding**: the silent samples added at the start and end. Gapless players remove them, so albums play without gaps between tracks.',
  },
  'mp3.vbri': {
    title: 'VBRI header',
    body: 'The Fraunhofer encoder\'s version of the Xing header. It sits 32 bytes after the side information and holds the frame count, byte count and a seek table.',
  },
  'mp3.xing.rest': {
    title: 'Unused bytes',
    body: 'The rest of the header frame. It must be as long as a real frame at this bitrate, so the encoder fills the space with zeros.',
  },
  'mp3.junk': {
    title: 'Data before the first frame',
    body: 'Bytes between the tags and the first audio frame. They can be leftovers from a bad tag editor or a broken download. Players skip them by searching for the next frame sync.',
  },
  'mp3.garbage': {
    title: 'Unparsed bytes',
    body: 'These bytes do not form valid MPEG frames. The file may be damaged, or two files may be joined. A player searches for the next frame sync and continues, so you may hear a short glitch.',
  },
};

export const mp3Parser: FormatParser = {
  id: 'mp3',
  detect,
  parse,
  explanations: { ...mp3Explanations, ...trailingTagExplanations, ...id3Explanations },
};
